import {
  Authenticated,
  AuthLoading,
  Unauthenticated,
  useConvexAuth,
  useMutation,
  useQuery,
} from "convex/react";
import { SignIn, SignUp } from "@clerk/react";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import type { MouseEvent } from "react";

import { api } from "../convex/_generated/api";
import type { Id } from "../convex/_generated/dataModel";
import { useNkuliAuth } from "./auth/nkuliAuthContext";
import { decideRouteAccess, isProtectedRoute } from "./auth/route-guard";
import { resolveSessionState } from "./auth/session";
import { CapabilityInstallationPage } from "./inbox/CapabilityInstallationPage";
import { BusinessSetupPage } from "./inbox/BusinessSetupPage";
import { ConfigurationAssistantPage } from "./inbox/ConfigurationAssistantPage";
import { CustomerAccountPage } from "./inbox/CustomerAccountPage";
import { InboxApp } from "./inbox/InboxApp";
import { CustomersPage } from "./customers/CustomersPage";
import { CustomerProfilePage } from "./customers/CustomerProfilePage";
import { NewCustomerPage } from "./customers/NewCustomerPage";
import { WhatsAppConnectionSetupPage } from "./inbox/WhatsAppConnectionSetupPage";
import type { BusinessMembership } from "./inbox/types";
import { navigateToAppPath } from "./navigation";
import { SupportConsole } from "./support/SupportConsole";
import { markNkuliPerformance } from "./performance";
import { WorkspaceOverview } from "./workspace/WorkspaceOverview";
import { WorkspaceErrorBoundary } from "./workspace/WorkspaceErrorBoundary";
import { WorkspacePlannedPage } from "./workspace/WorkspacePlannedPage";
import { WorkspaceSetup } from "./workspace/WorkspaceSetup";
import { WorkspaceShell } from "./workspace/WorkspaceShell";

export function App() {
  const { isLoading: convexLoading, isAuthenticated: convexAuthenticated } =
    useConvexAuth();
  const { providerLoading, providerAuthenticated, signOut } = useNkuliAuth();
  const pathname = useBrowserPathname();
  const previousProviderAuthenticated = useRef(false);
  const previousProviderReady = useRef(false);
  const previousConvexAuthenticated = useRef(false);
  const sessionState = resolveSessionState({
    providerLoading: providerLoading || convexLoading,
    providerAuthenticated,
    convexAuthenticated,
  });
  const routeDecision = decideRouteAccess({
    pathname,
    isLoading: sessionState === "loading",
    isAuthenticated: sessionState === "authenticated",
  });
  const redirectDestination =
    routeDecision.state === "redirect" ? routeDecision.destination : null;
  const appDestination =
    routeDecision.state === "allow" && pathname === "/callback"
      ? "/app"
      : redirectDestination;
  const showPublicClerkAuth =
    !isProtectedRoute(pathname) &&
    pathname !== "/login" &&
    !providerLoading &&
    !providerAuthenticated;

  useLayoutEffect(() => {
    document.documentElement.dataset.nkuliRoute = showPublicClerkAuth
      ? "auth"
      : "app";
  }, [showPublicClerkAuth]);

  useEffect(() => {
    if (!previousProviderReady.current && !providerLoading) {
      markNkuliPerformance("clerk_ready");
    }
    if (!previousProviderAuthenticated.current && providerAuthenticated) {
      markNkuliPerformance("auth_success");
    }
    if (
      !previousConvexAuthenticated.current &&
      convexAuthenticated &&
      !convexLoading
    ) {
      markNkuliPerformance("convex_authenticated");
    }
    previousProviderReady.current = !providerLoading;
    previousProviderAuthenticated.current = providerAuthenticated;
    previousConvexAuthenticated.current = convexAuthenticated;
  }, [
    convexAuthenticated,
    convexLoading,
    providerAuthenticated,
    providerLoading,
  ]);

  useEffect(() => {
    if (pathname === "/login") {
      document.title = "sign in — nkuli";
      return;
    }

    if (routeDecision.state === "loading") {
      document.title = "checking session — nkuli";
      return;
    }

    document.title =
      routeDecision.state === "allow" && pathname.startsWith("/support")
        ? "support — nkuli"
        : routeDecision.state === "allow" && pathname === "/app"
          ? "overview — nkuli"
          : routeDecision.state === "allow" && pathname === "/app/inbox"
            ? "inbox — nkuli"
            : "nkuli";
  }, [pathname, routeDecision.state]);

  useEffect(() => {
    if (appDestination) {
      navigateToAppPath(appDestination);
    }
  }, [appDestination]);

  if (pathname === "/login" && !providerAuthenticated) {
    return <ClerkAuthSurface mode="sign-in" />;
  }

  if (showPublicClerkAuth) {
    return <ClerkAuthSurface mode="sign-up" />;
  }

  if (routeDecision.state === "loading") {
    return <AuthTransition />;
  }

  return (
    <>
      <AuthLoading>
        <AuthTransition />
      </AuthLoading>
      <Unauthenticated>
        <ClerkAuthSurface mode="sign-up" />
      </Unauthenticated>
      <Authenticated>
        <AuthenticatedShell onSignOut={signOut} />
      </Authenticated>
    </>
  );
}

function AuthTransition() {
  return (
    <main aria-busy="true" className="auth-transition-shell">
      <div className="auth-transition-content">
        <img alt="nkuli" src="/assets/nkuli-wordmark-black.png" />
        <span aria-hidden="true" className="auth-transition-indicator" />
        <p role="status">Getting Nkuli ready</p>
        <small>Securing your workspace...</small>
      </div>
    </main>
  );
}

function ClerkAuthSurface({ mode }: { mode: "sign-in" | "sign-up" }) {
  const clerkAppearance = {
    elements: {
      card: "nkuli-clerk-card",
      logoBox: "nkuli-clerk-logo-box",
      logoImage: "nkuli-clerk-logo-image",
    },
  };

  return (
    <main className="clerk-auth-shell" onClickCapture={handleAuthLinkClick}>
      <div className="clerk-auth-frame">
        <div className="clerk-auth-brand">
          <img
            alt="nkuli"
            className="nkuli-auth-logo"
            src="/assets/nkuli-wordmark-black.png"
          />
        </div>
        {mode === "sign-in" ? (
          <SignIn
            appearance={clerkAppearance}
            oauthFlow="popup"
            routing="hash"
            signUpUrl="/"
          />
        ) : (
          <SignUp
            appearance={clerkAppearance}
            oauthFlow="popup"
            routing="hash"
            signInUrl="/login"
          />
        )}
      </div>
    </main>
  );
}

function handleAuthLinkClick(event: MouseEvent<HTMLElement>) {
  if (
    event.defaultPrevented ||
    event.metaKey ||
    event.ctrlKey ||
    event.shiftKey
  ) {
    return;
  }

  const anchor = (event.target as Element | null)?.closest("a[href]");
  if (!anchor || !(anchor instanceof HTMLAnchorElement)) return;

  const nextUrl = new URL(anchor.href, window.location.href);
  const isLocalAuthRoute =
    nextUrl.origin === window.location.origin &&
    (nextUrl.pathname === "/" || nextUrl.pathname === "/login");

  if (!isLocalAuthRoute || nextUrl.pathname === window.location.pathname) {
    return;
  }

  event.preventDefault();
  window.history.pushState({}, "", nextUrl);
  window.dispatchEvent(new PopStateEvent("popstate"));
}

function AuthenticatedShell({ onSignOut }: { onSignOut: () => void }) {
  const pathname = useBrowserPathname();
  const businesses = useQuery(api.businesses.listMine) as
    BusinessMembership[] | undefined;

  if (pathname === "/support" || pathname.startsWith("/support/")) {
    return <SupportConsole onSignOut={onSignOut} />;
  }

  return (
    <BusinessAccessShell
      businesses={businesses}
      onSignOut={onSignOut}
      pathname={pathname}
    />
  );
}

function BusinessAccessShell({
  businesses,
  onSignOut,
  pathname,
}: {
  businesses: BusinessMembership[] | undefined;
  onSignOut: () => void;
  pathname: string;
}) {
  const bootstrap = useMutation(api.businesses.bootstrapFirstBusiness);
  const bootstrapAttempted = useRef(false);
  const [bootstrapState, setBootstrapState] = useState<
    "idle" | "loading" | "error"
  >("idle");
  const [bootstrappedBusiness, setBootstrappedBusiness] =
    useState<BusinessMembership | null>(null);

  const bootstrapWorkspace = useCallback(async () => {
    setBootstrapState("loading");
    try {
      const result = await bootstrap({});
      if (!result?.businessId) throw new Error("BUSINESS_BOOTSTRAP_FAILED");

      setBootstrappedBusiness({
        businessId: result.businessId,
        name: null,
        onboardingStatus: "NOT_CONFIGURED",
        aiOnboardingState: "NOT_STARTED",
        connectionStatus: "NOT_CONNECTED",
        role: "OWNER",
      });
      setBootstrapState("idle");
    } catch {
      setBootstrapState("error");
    }
  }, [bootstrap]);

  useEffect(() => {
    if (businesses?.length !== 0 || bootstrapAttempted.current) return;

    bootstrapAttempted.current = true;
    void bootstrapWorkspace();
  }, [bootstrapWorkspace, businesses]);

  useEffect(() => {
    if (businesses !== undefined) {
      markNkuliPerformance("first_run_state_resolved");
    }
  }, [businesses]);

  const availableBusinesses =
    Array.isArray(businesses) && businesses.length > 0
      ? businesses
      : bootstrappedBusiness
        ? [bootstrappedBusiness]
        : null;

  if (availableBusinesses) {
    return (
      <AuthenticatedBusinessRoute
        businesses={availableBusinesses}
        onSignOut={onSignOut}
        pathname={pathname}
      />
    );
  }

  if (businesses === undefined) {
    return <AuthTransition />;
  }

  if (bootstrapState === "error") {
    return (
      <main className="workspace-bootstrap-state">
        <section role="alert">
          <span className="workspace-eyebrow">Workspace unavailable</span>
          <h1>We could not create your business workspace</h1>
          <p>Your account is safe. Retry the idempotent workspace setup.</p>
          <button onClick={() => void bootstrapWorkspace()} type="button">
            Retry workspace setup
          </button>
        </section>
      </main>
    );
  }

  return (
    <main aria-busy="true" className="workspace-bootstrap-state">
      <p role="status">Creating your business workspace...</p>
    </main>
  );
}

function AuthenticatedBusinessRoute({
  businesses,
  onSignOut,
  pathname,
}: {
  businesses: BusinessMembership[];
  onSignOut: () => void;
  pathname: string;
}) {
  const [selectedBusinessId, setSelectedBusinessId] = useState<
    Id<"businesses">
  >(() => initialSelectedBusinessId(businesses));
  const selectedBusiness =
    businesses.find((business) => business.businessId === selectedBusinessId) ??
    businesses[0];

  const selectBusiness = useCallback((businessId: Id<"businesses">) => {
    setSelectedBusinessId(businessId);
    window.sessionStorage.setItem("nkuli:selected-business", businessId);
  }, []);

  useEffect(() => {
    if (!isKnownWorkspacePath(pathname)) {
      navigateToAppPath("/app");
    }
  }, [pathname]);

  const setupPath = isWorkspaceSetupPath(pathname);

  return (
    <WorkspaceShell
      activePath={
        pathname === "/customers/new"
          ? "/customers/new"
          : isCustomerProfilePath(pathname)
            ? "/app/customers"
            : setupPath
              ? "/app/setup"
              : pathname
      }
      businesses={businesses}
      onBusinessChange={selectBusiness}
      onSignOut={onSignOut}
      selectedBusiness={selectedBusiness}
    >
      {({ selectedBusiness }) => (
        <WorkspaceErrorBoundary
          key={`${selectedBusiness.businessId}:${pathname}`}
        >
          {workspaceContent(pathname, selectedBusiness, onSignOut)}
        </WorkspaceErrorBoundary>
      )}
    </WorkspaceShell>
  );
}

function workspaceContent(
  pathname: string,
  business: BusinessMembership,
  onSignOut: () => void,
) {
  const customerContent = customerWorkspaceContent(pathname, business);
  if (customerContent) return customerContent;

  if (pathname === "/app/business-setup") {
    return (
      <BusinessSetupPage business={business} embedded onSignOut={onSignOut} />
    );
  }
  if (pathname === "/app/whatsapp-connection") {
    return (
      <WhatsAppConnectionSetupPage
        business={business}
        embedded
        onSignOut={onSignOut}
      />
    );
  }
  if (pathname === "/app/capabilities") {
    return (
      <CapabilityInstallationPage
        business={business}
        embedded
        onSignOut={onSignOut}
      />
    );
  }
  if (pathname === "/app/configuration-assistant") {
    return (
      <ConfigurationAssistantPage
        business={business}
        embedded
        onSignOut={onSignOut}
      />
    );
  }
  if (pathname === "/app/account") {
    return (
      <CustomerAccountPage business={business} embedded onSignOut={onSignOut} />
    );
  }
  if (pathname === "/app/setup" || pathname === "/app/whatsapp-first") {
    return <WorkspaceSetup business={business} />;
  }
  if (pathname === "/app/inbox") {
    return <InboxApp business={business} />;
  }
  if (isPlannedWorkspacePath(pathname)) {
    return <WorkspacePlannedPage path={pathname} />;
  }
  return <WorkspaceOverview business={business} />;
}

function customerWorkspaceContent(
  pathname: string,
  business: BusinessMembership,
) {
  if (pathname === "/app/customers") {
    return <CustomersPage business={business} />;
  }
  if (pathname === "/customers/new") {
    return <NewCustomerPage />;
  }
  if (isCustomerProfilePath(pathname)) {
    const customerId = decodeURIComponent(pathname.slice("/customers/".length));
    return <CustomerProfilePage business={business} customerId={customerId} />;
  }
  return null;
}

function isWorkspaceSetupPath(pathname: string) {
  return [
    "/app/setup",
    "/app/business-setup",
    "/app/whatsapp-first",
    "/app/whatsapp-connection",
    "/app/capabilities",
    "/app/configuration-assistant",
    "/app/account",
    ...plannedWorkspacePaths,
  ].includes(pathname);
}

const plannedWorkspacePaths = [
  "/app/orders",
  "/app/products",
  "/app/flows",
  "/app/broadcasts",
  "/app/sequences",
  "/app/triggers",
  "/app/tools",
];

function isPlannedWorkspacePath(pathname: string) {
  return plannedWorkspacePaths.includes(pathname);
}

function initialSelectedBusinessId(businesses: BusinessMembership[]) {
  const storedBusinessId = window.sessionStorage.getItem(
    "nkuli:selected-business",
  );
  const selected = businesses.find(
    (business) => business.businessId === storedBusinessId,
  );
  return selected?.businessId ?? businesses[0].businessId;
}

function isKnownWorkspacePath(pathname: string) {
  if (isCustomerProfilePath(pathname)) return true;

  return [
    "/app",
    "/callback",
    "/app/inbox",
    "/app/customers",
    "/customers/new",
    "/app/setup",
    "/app/business-setup",
    "/app/whatsapp-first",
    "/app/whatsapp-connection",
    "/app/capabilities",
    "/app/configuration-assistant",
    "/app/account",
    ...plannedWorkspacePaths,
  ].includes(pathname);
}

function isCustomerProfilePath(pathname: string) {
  return /^\/customers\/[^/]+$/.test(pathname) && pathname !== "/customers/new";
}

function useBrowserPathname() {
  const [pathname, setPathname] = useState(window.location.pathname);

  useEffect(() => {
    function syncPathname() {
      setPathname(window.location.pathname);
    }

    window.addEventListener("popstate", syncPathname);
    return () => window.removeEventListener("popstate", syncPathname);
  }, []);

  return pathname;
}
